import React, { useState, useEffect } from "react";
import { ThemeProvider, CssBaseline, Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, Avatar, Chip, CircularProgress } from "@mui/material";
import { Dashboard, People, Work, Payments, Logout, ManageSearch, Analytics, Gavel, Campaign, HowToReg, Psychology } from "@mui/icons-material";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { theme } from "./theme";
import { LoginView } from "./views/LoginView";
import { DashboardView } from "./views/DashboardView";
import { TechsView } from "./views/TechsView";
import { JobsView } from "./views/JobsView";
import { PayoutsView } from "./views/PayoutsView";
import { JobManagementView } from "./views/JobManagementView";
import { AnalyticsView } from "./views/AnalyticsView";
import { DisputesView } from "./views/DisputesView";
import { CustomersView } from "./views/CustomersView";
import { MarketingView } from "./views/MarketingView";
import { WaitlistView } from "./views/WaitlistView";
import InsightsView from "./views/InsightsView";

const DRAWER_WIDTH = 240;

const NAV_ITEMS = [
  { label: "Dashboard",    icon: <Dashboard />,     view: "dashboard" },
  { label: "Manage Jobs",  icon: <ManageSearch />,  view: "manage" },
  { label: "Jobs Log",     icon: <Work />,          view: "jobs" },
  { label: "Technicians",  icon: <People />,        view: "techs" },
  { label: "Customers",    icon: <People />,        view: "customers" },
  { label: "Payouts",      icon: <Payments />,      view: "payouts" },
  { label: "Analytics",    icon: <Analytics />,     view: "analytics" },
  { label: "AI Insights",  icon: <Psychology />,    view: "insights" },
  { label: "Marketing",    icon: <Campaign />,      view: "marketing" },
  { label: "Disputes",     icon: <Gavel />,         view: "disputes" },
  { label: "Waitlist",     icon: <HowToReg />,      view: "waitlist" },
];

export default function App() {
  const [activeView, setActiveView] = useState("dashboard");
  const [highlightJobId, setHighlightJobId] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const handleJobClick = (jobId: string) => {
    setHighlightJobId(jobId);
    setActiveView("manage");
  };

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (u) {
        const token = await u.getIdTokenResult();
        setUser(token.claims.admin ? u : null);
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
  }, []);

  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress color="primary" />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginView onLogin={() => {}} />
      </ThemeProvider>
    );
  }

  const renderView = () => {
    switch (activeView) {
      case "dashboard":  return <DashboardView onJobClick={handleJobClick} />;
      case "manage":     return <JobManagementView highlightJobId={highlightJobId} />;
      case "jobs":       return <JobsView />;
      case "techs":      return <TechsView />;
      case "customers":  return <CustomersView />;
      case "payouts":    return <PayoutsView />;
      case "analytics":  return <AnalyticsView />;
      case "insights":   return <InsightsView />;
      case "marketing":  return <MarketingView />;
      case "disputes":   return <DisputesView />;
      case "waitlist":   return <WaitlistView />;
      default:           return <DashboardView />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh" }}>

        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              bgcolor: "background.paper",
              borderRight: "1px solid rgba(255,255,255,0.06)",
            },
          }}
        >
          {/* Logo */}
          <Box sx={{ p: 3, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "-0.5px" }}>
              NeuroFlow
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              SageTech Ops Dashboard
            </Typography>
          </Box>

          <List sx={{ pt: 2 }}>
            {NAV_ITEMS.map((item) => (
              <ListItem key={item.view} disablePadding sx={{ mb: 0.5, px: 1 }}>
                <ListItemButton
                  selected={activeView === item.view}
                  onClick={() => { setActiveView(item.view); if (item.view !== "manage") setHighlightJobId(null); }}
                  sx={{
                    borderRadius: 2,
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "white",
                      "& .MuiListItemIcon-root": { color: "white" },
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Bottom — admin identity */}
          <Box sx={{ mt: "auto", p: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36, fontSize: 14 }}>N</Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>NeuroFlow Admin</Typography>
                <Typography variant="caption" color="text.secondary">SageTech HQ</Typography>
              </Box>
            </Box>
          </Box>
        </Drawer>

        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, bgcolor: "background.default", minHeight: "100vh" }}>
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              bgcolor: "background.default",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, textTransform: "capitalize" }}>
                {activeView}
              </Typography>
              <Chip
                label="● Winston-Salem"
                size="small"
                sx={{ bgcolor: "success.main", color: "white", fontWeight: 700, mr: 1 }}
              />
              <Chip label="LIVE" size="small" color="success" variant="outlined" sx={{ mr: 2 }} />
              <Chip
                label="Sign Out"
                size="small"
                icon={<Logout sx={{ fontSize: "14px !important" }} />}
                onClick={() => signOut(auth)}
                variant="outlined"
                sx={{ cursor: "pointer" }}
              />
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 3 }}>
            {renderView()}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
